// Apolo IDE — Plan charts data helpers (sprint plan-charts).
//
// Funções PURAS (sem React, sem recharts): montam séries de preset a partir
// dos blocos/estimativas, calculam métricas do dashboard e serializam um
// chart pra tabela markdown (export do sprint directive). Testável isolada.

import { PHASES } from './suggestions.js';
import { computePhaseEstimates } from './estimates.js';

export const CHART_TYPES = ['line', 'bar', 'area', 'pie'];

export const PRESETS = [
  { id: 'hours',  label: 'Horas por fase',     type: 'bar' },
  { id: 'roi',    label: 'Esforço (ROI) por fase', type: 'bar' },
  { id: 'blocks', label: 'Blocos por fase',     type: 'pie' },
  { id: 'kpi',    label: 'KPIs por fase',       type: 'bar' },
];

const PHASE_BY_ID = Object.fromEntries(PHASES.map((p) => [p.id, p]));

function countBlocksByPhase(blocks) {
  const counts = new Map();
  for (const b of Array.isArray(blocks) ? blocks : []) {
    const ph = b?.payload?._phase;
    if (ph && PHASE_BY_ID[ph]) counts.set(ph, (counts.get(ph) || 0) + 1);
  }
  return counts;
}

/**
 * Monta um chart de preset a partir dos blocos do plano.
 * @returns {{ type, title, series:[{name,data:[{x,y}]}], source }}
 */
export function buildPresetSeries(presetId, blocks, estimates) {
  const est = estimates || computePhaseEstimates(blocks);
  const preset = PRESETS.find((p) => p.id === presetId) || PRESETS[0];
  // Usa o id RESOLVIDO (preset.id) — quando presetId é desconhecido cai no
  // primeiro preset (hours), então branchar no raw presetId deixaria data vazio.
  const resolvedId = preset.id;
  let data = [];

  if (resolvedId === 'hours') {
    data = est.phases.map((p) => ({ x: `${p.emoji} ${p.label}`, y: p.hours }));
  } else if (resolvedId === 'roi') {
    // Proxy de esforço/retorno: usa horas por fase como magnitude (ROI textual
    // fica no título). Mantém comparável visualmente.
    data = est.phases.map((p) => ({ x: `${p.emoji} ${p.label}`, y: p.hours }));
  } else if (resolvedId === 'blocks') {
    const counts = countBlocksByPhase(blocks);
    data = PHASES.filter((p) => counts.has(p.id)).map((p) => ({ x: `${p.emoji} ${p.label}`, y: counts.get(p.id) }));
  } else if (resolvedId === 'kpi') {
    data = est.phases.map((p) => ({ x: `${p.emoji} ${p.label}`, y: Array.isArray(p.kpis) ? p.kpis.length : 0 }));
  }

  return {
    type: preset.type,
    title: preset.label,
    series: [{ name: preset.label, data }],
    source: `preset:${preset.id}`,
  };
}

/**
 * Métricas-resumo pros cards do topo da Charts View.
 */
export function chartMetrics(blocks, estimates) {
  const est = estimates || computePhaseEstimates(blocks);
  const list = Array.isArray(blocks) ? blocks : [];
  return {
    totalHours: est.totalHours,
    phaseCount: est.phases.length,
    blockCount: list.length,
  };
}

/**
 * Serializa um chart pra markdown (legenda + tabela). Usado no export do
 * sprint directive — a IA recebe os números reais.
 */
export function serializeChartToMarkdown(chart) {
  if (!chart) return '';
  const type = CHART_TYPES.includes(chart.type) ? chart.type : 'line';
  const title = chart.title || 'Gráfico';
  const series = Array.isArray(chart.series) ? chart.series : [];
  const lines = [`**Gráfico (${type}):** ${title}`, ''];

  // Coleta todos os rótulos x na ordem de aparição.
  const xs = [];
  for (const s of series) {
    for (const pt of (s.data || [])) {
      if (!xs.includes(pt.x)) xs.push(pt.x);
    }
  }
  if (xs.length === 0 || series.length === 0) {
    lines.push('_(sem dados)_');
    return lines.join('\n');
  }
  const cell = (v) => String(v ?? '').replace(/\|/g, '\\|');
  const header = ['Categoria', ...series.map((s) => s.name || 'Série')];
  lines.push(`| ${header.map(cell).join(' | ')} |`);
  lines.push(`| ${header.map(() => '---').join(' | ')} |`);
  for (const x of xs) {
    const row = [x, ...series.map((s) => {
      const pt = (s.data || []).find((d) => d.x === x);
      return pt ? pt.y : '';
    })];
    lines.push(`| ${row.map(cell).join(' | ')} |`);
  }
  return lines.join('\n');
}
